import React, { useState, useEffect, FC } from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import { Text } from '@contrast-security-inc/design-system-typography'
import {
  IconDelete,
  IconWarning,
  IconCheckSelected,
  theme
} from '@contrast-security-inc/design-system-foundations'
import { Row } from '../../../../../legacy-design-system/form'
import { useJiraIntegrationPage } from '../jira-integration-page.provider'
import { Button } from '@contrast-security-inc/design-system-buttons'
import { useDeleteConfigurationQuery } from '../../api/use-delete-configuration-query'
import { DeleteConfigurationDialog } from './delete-configuration-dialog'
import { useFormContext, useFormState } from 'react-hook-form'
import {
  getIntegrationsManageRoute,
  getIntegrationsRoute
} from '../../../../route'
import { ConfirmOnExitDialog } from './confirm-on-exit-dialog'
import {
  cancelAndRestorePreviousCredentials,
  updateManagingCredentials
} from '../state/jira-integration-page-actions'
import {
  hasManageCredentialsDraftChanges,
  updateAnyState
} from '../state/jira-integration-page.state'
import { JiraIntegrationForm } from '../jira-form-schema'
import { JiraFormFieldTypes } from '../jira-form-field-types'
import { DeleteCredentialDialog } from './delete-credential-dialog'
import { useDeleteCredentialQuery } from '../../api/use-delete-credential-query'
import { toast } from '@contrast-security-inc/design-system-alerts'
interface FooterProps {
  deleteIntegration: () => void
  credentialId: string
  credentialName: string
  testConnection: () => void
}

const ValidConnectionButton: FC = () => {
  const intl = useIntl()

  return (
    <Button
      className={css`
        margin-right: 5px;
      `}
      variant="secondary"
      isDisabled={true}
      data-testid="jira-connection-valid-button"
    >
      <span
        className={css`
          font-weight: bold;
          display: flex;
        `}
      >
        <IconCheckSelected color="green50" />{' '}
        <Text font="body">
          {intl.formatMessage({
            id: 'title_integrationsSettings_statusConnected'
          })}
        </Text>
      </span>
    </Button>
  )
}

const InvalidConnectionButton: FC = () => {
  const intl = useIntl()
  const cssButton = css`
    font-weight: bold;
    color: ${theme.color.red50};
    display: flex;
  `

  return (
    <Button
      className={css`
        margin-right: 5px;
      `}
      isDisabled={true}
      type="button"
      data-testid="jira-connection-invalid-button"
    >
      <span className={cssButton}>
        <IconWarning color="red50" />
        <Text font="body">
          {intl.formatMessage({
            id: 'title_integrationsSettings_unableToConnect'
          })}
        </Text>
      </span>
    </Button>
  )
}

const TestingInProgressConnectionButton: FC = () => {
  const intl = useIntl()

  return (
    <Button
      className={css`
        margin-right: 5px;
      `}
      isDisabled={true}
      type="button"
    >
      <Text font="body" color="white">
        {intl.formatMessage({ id: 'action_testConnection' })}
      </Text>
    </Button>
  )
}

const UntestedConnectionButton: FC<{ testConnection: () => void }> = ({
  testConnection
}) => {
  const intl = useIntl()
  const { watch } = useFormContext<JiraIntegrationForm>()
  const [isDisabled, setIsDisabled] = useState(true)
  const credentials = watch([
    JiraFormFieldTypes.CREDENTIALS_HOST,
    JiraFormFieldTypes.CREDENTIALS_USERNAME,
    JiraFormFieldTypes.CREDENTIALS_PASSWORD
  ])

  useEffect(() => {
    setIsDisabled(!credentials[0] || !credentials[1] || !credentials[2])
  }, [credentials])

  return (
    <Button
      className={css`
        margin-right: 5px;
      `}
      isDisabled={isDisabled}
      onClick={testConnection}
      type="button"
      data-testid="jira-connection-test-button"
    >
      <Text font="body" color="white">
        {intl.formatMessage({ id: 'action_testConnection' })}
      </Text>
    </Button>
  )
}

export const Footer = ({
  testConnection,
  credentialId,
  credentialName
}: FooterProps) => {
  const intl = useIntl()
  const formContext = useFormContext<JiraIntegrationForm>()
  const { getValues, setValue } = formContext
  const { isValid, isDirty } = useFormState()

  const [isDeleteQueryEnabled, setIsDeleteQueryEnabled] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeleteCredentialModalOpen, setIsDeleteCredentialModalOpen] =
    useState(false)
  const [isDeleteCredentialQueryEnabled, setIsDeleteCredentialQueryEnabled] =
    useState(false)
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const { state: pageState, dispatch } = useJiraIntegrationPage()

  const {
    isEdit,
    organizationUuid,
    isManagingCredentials,
    bugtrackerId: configurationId,
    connectionStatus
  } = pageState

  // Checks if all of the credentials fields are dirty so the test connection
  // button can be enabled
  useDeleteConfigurationQuery({
    organizationUuid,
    configurationId,
    isEnabled: isDeleteQueryEnabled,
    successCallback: () => {
      location.href = getIntegrationsRoute()
    }
  })

  useDeleteCredentialQuery({
    organizationUuid,
    credentialId,
    isEnabled: isDeleteCredentialQueryEnabled,
    successCallback: () => {
      toast(
        intl.formatMessage({
          id: 'detail_integrationsSettings_successfullyDeletedJiraCredential'
        })
      )
      location.href = `${getIntegrationsManageRoute()}/jira`
    },
    errorCallback: () => {
      setIsDeleteCredentialQueryEnabled(false)
    }
  })

  const setCredentialsHasUpdate = () =>
    dispatch(
      updateAnyState({
        hasCredentialsUpdate: true
      })
    )
  const redirectToListPage = () => (location.href = getIntegrationsRoute())

  const cancelAndRestore = () => {
    cancelAndRestorePreviousCredentials({
      dispatch,
      formContext,
      pageState
    })
  }

  const hideManagingCredentialsSubForm = () => {
    updateManagingCredentials({
      dispatch,
      formContext,
      pageState,
      isManagingCredentials: false
    })
  }

  const onCancelButtonClick = () => {
    if (isManagingCredentials) {
      //  Prompt confirm if managing credentials with a valid connection
      if (
        connectionStatus === 'valid' &&
        hasManageCredentialsDraftChanges(formContext, pageState)
      ) {
        setIsCancelModalOpen(true)
      } else {
        cancelAndRestore()
        hideManagingCredentialsSubForm()
      }
    } else if (isDirty) {
      setIsCancelModalOpen(true)
    } else {
      redirectToListPage()
    }
  }

  const getJustifyContent = () => {
    return (isEdit && !isManagingCredentials) || isManagingCredentials
      ? 'space-between'
      : 'flex-end'
  }

  return (
    <div
      className={css`
        display: flex;
        justify-content: ${getJustifyContent()};
      `}
    >
      {isManagingCredentials && (
        <Button
          className={css`
            margin-right: 5px;
            background: none;
          `}
          variant="destructive"
          onClick={() => setIsDeleteCredentialModalOpen(true)}
        >
          <span
            className={css`
              font-weight: bold;
              display: flex;
            `}
          >
            <IconDelete />
            <Text font="body">
              {intl.formatMessage({
                id: 'action_integrationsSettings_deleteCredential'
              })}
            </Text>
          </span>
        </Button>
      )}

      {isEdit && !isManagingCredentials && (
        <div
          className={css`
            cursor: pointer;
            display: flex;
          `}
          onClick={() => setIsDeleteModalOpen(true)}
        >
          <IconDelete testId="delete_jira_configuration" />
          <Text font="body">
            {intl.formatMessage({
              id: 'action_integrationsSettings_deleteConfig'
            })}
          </Text>
        </div>
      )}
      <div>
        <Row>
          {connectionStatus === 'testing' && (
            <span
              className={css`
                align-self: center;
                margin-right: 5px;
              `}
            >
              <Text font="body">
                {intl.formatMessage({ id: 'detail_waitingMessage' })}
              </Text>
            </span>
          )}
          <Button
            data-testid="cancel-button"
            variant="secondary"
            type="button"
            className={css`
              margin-right: 5px;
            `}
            onClick={onCancelButtonClick}
          >
            <Text font="body">
              {intl.formatMessage({ id: 'action_cancel' })}
            </Text>
          </Button>
          {connectionStatus === 'valid' && <ValidConnectionButton />}
          {connectionStatus === 'invalid' && <InvalidConnectionButton />}
          {connectionStatus === 'untested' && (
            <UntestedConnectionButton testConnection={testConnection} />
          )}
          {connectionStatus === 'testing' && (
            <TestingInProgressConnectionButton />
          )}
          {isManagingCredentials && (
            <Button
              variant="secondary"
              data-testid="jira-form-credentials-done-button"
              disabled={connectionStatus !== 'valid'}
              onClick={() => {
                // if we switch the credentials we need to clear the project and issue
                setValue(JiraFormFieldTypes.PROJECT, '')
                setValue(JiraFormFieldTypes.ISSUE_TYPE, '')
                setValue(JiraFormFieldTypes.PROJECT_FIELDS, [])
                setCredentialsHasUpdate()
                hideManagingCredentialsSubForm()
              }}
            >
              <Text font="body">
                {intl.formatMessage({ id: 'action_done' })}
              </Text>
            </Button>
          )}
          {!isManagingCredentials && (
            <Button
              variant="secondary"
              type="submit"
              isDisabled={connectionStatus !== 'valid' || !isValid}
              data-testid="jira-form-submit-button"
            >
              <Text font="body">
                {intl.formatMessage({ id: 'action_save' })}
              </Text>
            </Button>
          )}
        </Row>
      </div>
      <DeleteConfigurationDialog
        isOpen={isDeleteModalOpen}
        integrationName={getValues('name')}
        onCancel={() => setIsDeleteModalOpen(false)}
        onConfirm={() => setIsDeleteQueryEnabled(true)}
      />
      <DeleteCredentialDialog
        isOpen={isDeleteCredentialModalOpen}
        credentialName={credentialName}
        onCancel={() => setIsDeleteCredentialModalOpen(false)}
        onConfirm={() => setIsDeleteCredentialQueryEnabled(true)}
      />
      <ConfirmOnExitDialog
        isOpen={isCancelModalOpen}
        onCancel={() => {
          setIsCancelModalOpen(false)
          cancelAndRestore()
          hideManagingCredentialsSubForm()
          if (!isManagingCredentials) {
            redirectToListPage()
          }
        }}
        onConfirm={() => {
          setIsCancelModalOpen(false)
          setCredentialsHasUpdate()
          hideManagingCredentialsSubForm()
        }}
      />
    </div>
  )
}
